import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { collection, query, where, getDocs, doc, getDoc, writeBatch } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { normalizePassportNumber } from '../utils/passportUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { calculateAge } from '../utils/dateUtils';
import { useAccommodationStore } from '../stores/accommodationStore';
import { Search, ArrowLeft, Calendar, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';

type ProcessingStatus = 'pending' | 'processing' | 'success' | 'error';

type PilgrimRow = {
  passport: string;
  family_link: string;
  gender?: string;
  room?: number;
  status: ProcessingStatus;
  result?: string;
  pilgrimId?: string;
  pilgrimName?: string;
  guideId?: string;
  guideName?: string;
  organizerId?: string;
  organizerName?: string;
  dateOfBirth?: string;
  belongsToHotel?: boolean;
  isAlreadyAccommodated?: boolean;
  checkInDate?: string;
  checkOutDate?: string;
  checkInTimestamp?: number;
  checkOutTimestamp?: number;
  roomType?: string;
  country?: string;
};

type FamilyGroup = {
  familyLink: string;
  members: PilgrimRow[];
  size: number;
};

export const BulkEasyAccommodationStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { translations } = useLanguage();
  const { tables } = useAccommodationStore();
  
  const [rows, setRows] = useState<PilgrimRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tableId, setTableId] = useState<string | null>(null);
  const [tableName, setTableName] = useState<string>('');
  const [hotelName, setHotelName] = useState<string>('');
  const [roomTextFilter, setRoomTextFilter] = useState<string>('');
  const [sameOrganizerOnly, setSameOrganizerOnly] = useState<boolean>(false); // State for same organizer option
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentStage, setCurrentStage] = useState<string>('Loading data');
  const [overallProgress, setOverallProgress] = useState<number>(0);
  const [processedCount, setProcessedCount] = useState<number>(0);
  const [totalToProcess, setTotalToProcess] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [familyGroups, setFamilyGroups] = useState<FamilyGroup[]>([]);
  const [processedPilgrimIds, setProcessedPilgrimIds] = useState<Set<string>>(new Set());
  
  // Add a new state for pilgrim country map
  const [pilgrimCountryMap, setPilgrimCountryMap] = useState<Map<string, string>>(new Map());

  // Filter rows based on search term
  const filteredRows = rows.filter(row => 
    row.passport.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (row.pilgrimName && row.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (row.result && row.result.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const initialize = async () => {
    if (!location.state || !location.state.fileData || !location.state.tableId) {
      navigate('/accommodation');
      return;
    }

    setIsLoading(true);
    setTableId(location.state.tableId);
    
    // Get room text filter from location state if available
    if (location.state.roomTextFilter) {
      setRoomTextFilter(location.state.roomTextFilter);
    }
    
    // Get sameOrganizerOnly flag from location state if available
    if (typeof location.state.sameOrganizerOnly === 'boolean') {
      setSameOrganizerOnly(location.state.sameOrganizerOnly);
    }
    
    try {
      // Get table information
      const tableRef = doc(firestore, 'AccommodationTables', location.state.tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        throw new Error('Accommodation table not found');
      }
      
      const tableData = tableDoc.data();
      setTableName(tableData.tn || 'Unknown Table');
      setHotelName(tableData.hn || 'Unknown Hotel');
      
      // Parse Excel data
      setCurrentStage('Reading file data');
      const workbook = XLSX.read(location.state.fileData, { type: 'binary' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(sheet);
      
      // Validate and format rows
      const formattedRows: PilgrimRow[] = [];
      const errors: string[] = [];
      const passportMap = new Map<string, number>(); // For tracking duplicates
      
      for (const row of jsonData) {
        const passport = typeof row.passport === 'string' ? row.passport.trim() : String(row.passport).trim();
        const familyLink = typeof row.family_link === 'string' ? row.family_link.trim() : String(row.family_link === undefined ? '' : row.family_link).trim();
        
        if (!passport) {
          errors.push('Missing passport number in row: ' + JSON.stringify(row));
          continue;
        }
        
        const normalizedPassport = normalizePassportNumber(passport);
        
        // Check for duplicate passports
        if (passportMap.has(normalizedPassport)) {
          // Skip duplicate passport entries
          console.log(`Skipping duplicate passport: ${normalizedPassport}`);
          continue;
        }
        
        // Track this passport
        passportMap.set(normalizedPassport, formattedRows.length);
        
        formattedRows.push({
          passport: normalizedPassport,
          family_link: familyLink,
          status: 'pending',
          room: undefined,
          gender: undefined
        });
      }
      
      if (errors.length > 0) {
        console.error('Validation errors:', errors);
      }
      
      setRows(formattedRows);
      setTotalToProcess(formattedRows.length);
      setIsInitialized(true);
      
      // Start processing
      await processAllPilgrims(formattedRows, location.state.tableId, tableData);
      
    } catch (error: any) {
      console.error('Error initializing:', error);
      setRows(prev => prev.map(row => ({
        ...row,
        status: 'error',
        result: `Error initializing: ${error.message}`
      })));
    } finally {
      setIsLoading(false);
    }
  };

  const processAllPilgrims = async (pilgrims: PilgrimRow[], tableId: string, tableData: any) => {
    try {
      setCurrentStage('Finding pilgrims');
      
      // Step 1: Find all pilgrims and enrich with data
      const enrichedPilgrims = await findPilgrims(pilgrims, tableData.hn);
      setRows(enrichedPilgrims);
      setOverallProgress(20);
      
      // Step 2: Group by family link
      setCurrentStage('Grouping families');
      const familyGroups = groupPilgrimsByFamilyLink(enrichedPilgrims);
      setFamilyGroups(familyGroups);
      console.log('Family groups:', familyGroups);
      setOverallProgress(40);
      
      // Step 3: Sort groups by size (largest first)
      familyGroups.sort((a, b) => b.size - a.size);
      
      // Step 4: Process accommodations
      await accommodatePilgrims(familyGroups, tableId, tableData);
      
      setOverallProgress(100);
      setCurrentStage('Completed');
      
    } catch (error: any) {
      console.error('Error processing pilgrims:', error);
      setRows(prev => prev.map(row => ({
        ...row,
        status: 'error',
        result: `Error processing: ${error.message}`
      })));
    }
  };

  const findPilgrims = async (pilgrims: PilgrimRow[], hotelName: string): Promise<PilgrimRow[]> => {
    const enrichedRows = [...pilgrims];
    let processed = 0;
    
    // Create batches of passport numbers
    const batchSize = 10;
    const batches = [];
    
    for (let i = 0; i < pilgrims.length; i += batchSize) {
      batches.push(pilgrims.slice(i, i + batchSize).map(p => p.passport));
    }
    
    // Fetch all PilgrimSelector documents first
    const selectorRef = collection(firestore, 'PilgrimSelector');
    const selectorSnapshot = await getDocs(selectorRef);
    
    // Create maps for quick lookups
    const selectorByPassport = new Map();
    const pilgrimCountryMap = new Map<string, string>();
    
    // Process selector documents
    selectorSnapshot.forEach(doc => {
      const selectorData = doc.data();
      if (selectorData.pilgrimData?.passportNumber && selectorData.status === 'active') {
        const passport = normalizePassportNumber(selectorData.pilgrimData.passportNumber);
        selectorByPassport.set(passport, { ...selectorData, id: doc.id });
        
        // Store pilgrim country information
        if (selectorData.pilgrimData.residenceCountry) {
          pilgrimCountryMap.set(selectorData.pilgrimId, selectorData.pilgrimData.residenceCountry);
        }
      }
    });
    
    // Store the country map for later use
    setPilgrimCountryMap(pilgrimCountryMap);
    
    for (const batchPassports of batches) {
      if (batchPassports.length === 0) continue;
      
      try {
        // Process each passport
        for (const passport of batchPassports) {
          // Look up the selector data from our map
          const selectorData = selectorByPassport.get(passport);
          
          if (selectorData) {
            // Find matching rows in our input data
            const matchingRows = enrichedRows.filter(
              row => row.passport === normalizePassportNumber(passport)
            );
            
            for (const row of matchingRows) {
              // Check if pilgrim belongs to this hotel
              const belongsToHotel = selectorData.hotels && selectorData.hotels.includes(hotelName);
              
              // Check if pilgrim is already accommodated in this hotel
              const isAccommodated = selectorData.isAccommodated && 
                                     selectorData.isAccommodated[hotelName] === true;
              
              // Find room type, check-in and check-out dates for this hotel
              let roomType = '';
              let checkInDate = '';
              let checkOutDate = '';
              let checkInTimestamp = 0;
              let checkOutTimestamp = 0;
              
              if (belongsToHotel) {
                // Determine which hotel type this is (main, shifting, madinah)
                if (selectorData.packageData.mainMakkah.hotel === hotelName) {
                  checkInDate = selectorData.packageData.mainMakkah.startDate;
                  checkOutDate = selectorData.packageData.mainMakkah.endDate;
                  roomType = selectorData.packageData.mainMakkah.roomType;
                } else if (selectorData.packageData.shiftingMakkah && 
                           selectorData.packageData.shiftingMakkah.hotel === hotelName) {
                  checkInDate = selectorData.packageData.shiftingMakkah.startDate;
                  checkOutDate = selectorData.packageData.shiftingMakkah.endDate;
                  roomType = selectorData.packageData.shiftingMakkah.roomType;
                } else if (selectorData.packageData.madinah.hotel === hotelName) {
                  checkInDate = selectorData.packageData.madinah.startDate;
                  checkOutDate = selectorData.packageData.madinah.endDate;
                  roomType = selectorData.packageData.madinah.roomType;
                }
                
                // Convert dd/mm/yyyy to timestamps
                if (checkInDate && checkOutDate) {
                  const [ciDay, ciMonth, ciYear] = checkInDate.split('/').map(Number);
                  const [coDay, coMonth, coYear] = checkOutDate.split('/').map(Number);
                  
                  checkInTimestamp = new Date(ciYear, ciMonth - 1, ciDay).getTime();
                  checkOutTimestamp = new Date(coYear, coMonth - 1, coDay).getTime();
                }
              }
              
              // Update the row with all necessary information
              row.pilgrimId = selectorData.pilgrimId;
              row.pilgrimName = selectorData.pilgrimData.name;
              const rawGender = selectorData.pilgrimData.gender?.toLowerCase();
              row.gender = rawGender === 'male' ? 'male' : rawGender === 'female' ? 'female' : undefined;
              row.dateOfBirth = selectorData.pilgrimData.dateOfBirth;
              row.guideId = selectorData.guideId;
              row.guideName = selectorData.guideData.name;
              row.organizerId = selectorData.organizerId;
              row.organizerName = selectorData.organizerData.name;
              row.belongsToHotel = belongsToHotel;
              row.isAlreadyAccommodated = isAccommodated;
              row.checkInDate = checkInDate;
              row.checkOutDate = checkOutDate;
              row.checkInTimestamp = checkInTimestamp;
              row.checkOutTimestamp = checkOutTimestamp;
              row.roomType = roomType; // Store the room type from package
              row.country = selectorData.pilgrimData.residenceCountry; // Add country information
              
              // Set error status if already accommodated or doesn't belong to hotel
              if (isAccommodated) {
                row.status = 'error';
                row.result = `Pilgrim is already accommodated in ${hotelName}`;
              } else if (!belongsToHotel) {
                row.status = 'error';
                row.result = `Pilgrim is not assigned to ${hotelName} in their package`;
              } else if (!selectorData.pilgrimData.residenceCountry) {
                // Add additional validation to ensure country is present
                row.status = 'error';
                row.result = 'Pilgrim has no country information - cannot be accommodated';
              }
              
              // Log what we found
              console.log(`Found in PilgrimSelector: ${row.pilgrimName}, Guide: ${row.guideName}, Room Type: ${roomType}, Stay period: ${checkInDate}-${checkOutDate}, Belongs: ${row.belongsToHotel}, Accommodated: ${row.isAlreadyAccommodated}, Country: ${row.country}`);
            }
          } else {
            // If not found in PilgrimSelector, mark as error
            const noMatchRows = enrichedRows.filter(
              row => row.passport === normalizePassportNumber(passport)
            );
            
            for (const row of noMatchRows) {
              row.status = 'error';
              row.result = 'Pilgrim not found or not linked to a guide';
            }
          }
        }
        
        processed += batchPassports.length;
        setProcessedCount(processed);
        
      } catch (error: any) {
        console.error('Error processing batch:', error, batchPassports);
        
        // Mark all rows in the batch as error
        for (const passport of batchPassports) {
          const affectedRows = enrichedRows.filter(
            row => row.passport === normalizePassportNumber(passport)
          );
          
          for (const row of affectedRows) {
            row.status = 'error';
            row.result = `Error: ${error.message}`;
          }
        }
      }
    }
    
    return enrichedRows;
  };

  const groupPilgrimsByFamilyLink = (pilgrims: PilgrimRow[]): FamilyGroup[] => {
    // Group by family_link, with empty family_link counted as individual "families"
    const groups: Record<string, PilgrimRow[]> = {};
    
    // First, handle pilgrims with family links
    pilgrims.filter(p => p.family_link).forEach(pilgrim => {
      if (!groups[pilgrim.family_link]) {
        groups[pilgrim.family_link] = [];
      }
      
      groups[pilgrim.family_link].push(pilgrim);
    });
    
    // Then individual pilgrims (without family links)
    pilgrims.filter(p => !p.family_link).forEach(pilgrim => {
      // Use passport as unique key for individuals
      const key = `individual-${pilgrim.passport}`;
      groups[key] = [pilgrim];
    });
    
    // Convert to array of family groups
    return Object.entries(groups).map(([familyLink, members]) => ({
      familyLink,
      members,
      size: members.length
    }));
  };

  // Helper function to sanitize data before sending to Firestore
  const sanitizeRoomsData = (rooms: any[]) => {
    return rooms.map(room => {
      // Ensure room object has all required properties
      const sanitizedRoom = {
        ...room,
        n: room.n || 0,
        c: room.c || 0,
        o: room.o || 0,
        t: room.t || '',
      };

      // Ensure beds array is properly formatted
      if (Array.isArray(sanitizedRoom.b)) {
        sanitizedRoom.b = sanitizedRoom.b.map((bed: any) => {
          // Sanitize bed object
          const sanitizedBed = { ...bed };
          
          // Ensure assignments array exists and is sanitized
          if (Array.isArray(sanitizedBed.a)) {
            sanitizedBed.a = sanitizedBed.a.map((assignment: any) => {
              // Remove any undefined values from assignment
              return Object.entries(assignment).reduce((acc: any, [key, value]) => {
                if (value !== undefined) {
                  acc[key] = value;
                }
                return acc;
              }, {});
            });
          } else {
            sanitizedBed.a = [];
          }
          
          return sanitizedBed;
        });
      } else {
        sanitizedRoom.b = [];
      }

      return sanitizedRoom;
    });
  };

  // Helper function to get a safe age value
  const getAge = (dateOfBirth?: string): number => {
    if (!dateOfBirth) return 30; // Default age if no DOB
    
    try {
      // Check if dateOfBirth is in DD/MM/YYYY format
      const isValidFormat = /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateOfBirth);
      if (!isValidFormat) return 30;
      
      const age = calculateAge(dateOfBirth);
      return isNaN(age) ? 30 : age;
    } catch (error) {
      console.warn('Error calculating age:', error, 'DOB:', dateOfBirth);
      return 30; // Default fallback
    }
  };

  // Helper function to check if there's a date conflict between assignments
  const hasDateConflict = (existingAssignment: any, checkInTimestamp: number, checkOutTimestamp: number): boolean => {
    let existingCheckIn, existingCheckOut;
    
    // Handle both string dates and timestamps
    if (typeof existingAssignment.ci === 'string' && existingAssignment.ci.includes('/')) {
      // Parse DD/MM/YYYY format
      const [day, month, year] = existingAssignment.ci.split('/').map(Number);
      existingCheckIn = new Date(year, month - 1, day).getTime();
    } else {
      // It's already a timestamp
      existingCheckIn = existingAssignment.ci;
    }
    
    if (typeof existingAssignment.co === 'string' && existingAssignment.co.includes('/')) {
      // Parse DD/MM/YYYY format
      const [day, month, year] = existingAssignment.co.split('/').map(Number);
      existingCheckOut = new Date(year, month - 1, day).getTime();
    } else {
      // It's already a timestamp
      existingCheckOut = existingAssignment.co;
    }
    
    // No conflict if check-out is before existing check-in or check-in is after existing check-out
    return !(checkOutTimestamp <= existingCheckIn || checkInTimestamp >= existingCheckOut);
  };

  // Convert room type string to the shortened format used in the table
  const convertRoomTypeToShort = (roomType: string): 'd' | 't' | 'q' => {
    switch (roomType.toLowerCase()) {
      case 'double': return 'd';
      case 'triple': return 't';
      case 'quadruple': return 'q';
      default: return 'd'; // Default fallback
    }
  };

  // Check if room number contains the room text filter
  const roomMatchesTextFilter = (roomNumber: number, filter: string): boolean => {
    if (!filter) return true; // If no filter, match all rooms
    return roomNumber.toString().includes(filter);
  };

  const accommodatePilgrims = async (familyGroups: FamilyGroup[], tableId: string, tableData: any) => {
    try {
      setCurrentStage('Assigning rooms');
      
      // Get available rooms from the table - clone to avoid mutating store data
      const rooms = JSON.parse(JSON.stringify(tableData.r));
      let assignedCount = 0;
      
      // Sort rooms by number (ascending)
      rooms.sort((a: any, b: any) => a.n - b.n);
      
      // Filter rooms by text filter if provided
      const filteredRooms = roomTextFilter
        ? rooms.filter((room: any) => roomMatchesTextFilter(room.n, roomTextFilter))
        : rooms;
      
      console.log(`Using ${filteredRooms.length} rooms out of ${rooms.length} total rooms with filter: '${roomTextFilter || 'none'}'`);
      
      // Process family groups first
      const realFamilies = familyGroups.filter(group => 
        !group.familyLink.startsWith('individual-') && group.members.length > 0
      );
      
      const individuals = familyGroups.filter(group => 
        group.familyLink.startsWith('individual-') && group.members.length > 0
      );
      
      console.log(`Processing ${realFamilies.length} family groups and ${individuals.length} individuals`);
      
      // Track processed pilgrim IDs to avoid duplicates
      const processedIds = new Set<string>();
      
      // Process in batches to update UI
      const batch = writeBatch(firestore);
      
      // First, process families
      for (const family of realFamilies) {
        const familyMembers = family.members.filter(m => 
          m.pilgrimId && 
          m.status !== 'error' && 
          !processedIds.has(m.pilgrimId) &&
          m.belongsToHotel === true && 
          m.isAlreadyAccommodated !== true &&
          m.country // Ensure country exists
        );
        
        if (familyMembers.length === 0) continue;
        
        const familySize = familyMembers.length;
        
        // Get family room type from first member (all members should have the same type)
        const familyRoomType = convertRoomTypeToShort(familyMembers[0].roomType || 'double');
        
        // Get family date range
        // We'll use the first member's dates as the date range for the entire family
        const firstMember = familyMembers[0];
        const familyCheckIn = firstMember.checkInDate || '';
        const familyCheckOut = firstMember.checkOutDate || '';
        
        if (!familyCheckIn || !familyCheckOut) {
          // Skip family if we don't have valid dates
          for (const member of familyMembers) {
            member.status = 'error';
            member.result = 'Missing check-in or check-out date information';
          }
          continue;
        }
        
        // Check if family has mixed genders
        const familyGenders = new Set(familyMembers.map(m => m.gender === 'male' ? 'M' : m.gender === 'female' ? 'F' : 'U'));
        const hasMixedGenders = familyGenders.size > 1;
        
        // Get family country (should be the same for all members)
        const familyCountry = familyMembers[0].country;

        // Get family organizer (should be the same for all members)
        const familyOrganizerId = familyMembers[0].organizerId;
        
        console.log(`Family check-in: ${familyCheckIn}, check-out: ${familyCheckOut}, size: ${familySize}, mixed genders: ${hasMixedGenders}, room type: ${familyRoomType}, country: ${familyCountry}, organizer: ${familyOrganizerId}`);
        
        // Find a suitable room for the family
        let roomFound = false;
        
        // First try to find a room matching the exact room type
        for (const room of filteredRooms) {
          // Skip rooms with wrong room type
          if (room.t !== familyRoomType) continue;
          
          // Skip rooms that are too small
          if (room.c < familySize) continue;
          
          // Check if the room has enough available beds for this time period
          let availableBedCount = 0;
          const availableBedIndices: number[] = [];
          
          // Loop through all beds in the room
          for (let bedIndex = 0; bedIndex < room.b.length; bedIndex++) {
            const bed = room.b[bedIndex];
            
            // If bed has no assignments, it's definitely available
            if (!bed.a || bed.a.length === 0) {
              availableBedCount++;
              availableBedIndices.push(bedIndex);
              continue;
            }
            
            // Check all assignments to see if any overlap with our date range
            let hasConflict = false;
            for (const assignment of bed.a) {
              if (hasDateConflict(assignment, firstMember.checkInTimestamp || 0, firstMember.checkOutTimestamp || 0)) {
                hasConflict = true;
                break;
              }
            }
            
            // If no conflict, this bed is available
            if (!hasConflict) {
              availableBedCount++;
              availableBedIndices.push(bedIndex);
            }
          }
          
          // Get existing assignments in this room that overlap with the family's stay period
          const existingAssignments = room.b
            .flatMap((bed: any) => bed.a || [])
            .filter((a: any) => hasDateConflict(a, firstMember.checkInTimestamp || 0, firstMember.checkOutTimestamp || 0));
          
          const roomIsEmpty = existingAssignments.length === 0;
          
          // Check for country conflicts when room is not empty
          let hasCountryConflict = false;
          if (!roomIsEmpty && familyCountry) {
            // Get countries of existing occupants using the pilgrimCountryMap
            for (const occupant of existingAssignments) {
              const country = pilgrimCountryMap.get(occupant.pid);
              if (country && country !== familyCountry) {
                hasCountryConflict = true;
                break;
              }
            }
            
            if (hasCountryConflict) {
              console.log(`Skipping room ${room.n} due to country conflict: family ${familyCountry}, occupants have different countries`);
              continue;
            }
          }
          
          // Check for organizer conflicts if enabled
          let hasOrganizerConflict = false;
          if (sameOrganizerOnly && !roomIsEmpty && familyOrganizerId) {
            const willFullyOccupyRoom = familySize === room.c;
            
            // Only check for organizer conflict if we're not fully occupying the room
            if (!willFullyOccupyRoom) {
              for (const occupant of existingAssignments) {
                if (occupant.oid && occupant.oid !== familyOrganizerId) {
                  hasOrganizerConflict = true;
                  break;
                }
              }
              
              if (hasOrganizerConflict) {
                console.log(`Skipping room ${room.n} due to organizer conflict: family ${familyOrganizerId}, occupants have different organizers`);
                continue;
              }
            }
          }
          
          // RULE IMPLEMENTATION:
          // 1. Family (mixed gender) -> goes to empty room
          // 4. Less than room capacity + mixed gender -> not allowed
          
          // Check if the family can be assigned to this room based on the rules
          let canAssignFamily = false;
          
          if (hasMixedGenders) {
            // Mixed gender family can only go to empty room or must fully occupy the room
            canAssignFamily = roomIsEmpty || (familySize === room.c && availableBedCount >= familySize);
          } else {
            // Single gender family can go to empty room or with same gender
            if (roomIsEmpty) {
              canAssignFamily = availableBedCount >= familySize;
            } else {
              // Check if existing occupants are the same gender
              const existingGender = existingAssignments.length > 0 ? existingAssignments[0].g : null;
              const familyGender = familyMembers[0].gender === 'male' ? 'M' : 'F';
              
              if (existingGender === null || existingGender === familyGender) {
                canAssignFamily = availableBedCount >= familySize;
              }
            }
          }
          
          if (canAssignFamily) {
            let assignedToRoom = 0;
            
            for (let i = 0; i < familyMembers.length && i < availableBedIndices.length; i++) {
              const member = familyMembers[i];
              const bedIndex = availableBedIndices[i];
              
              if (member.pilgrimId && !processedIds.has(member.pilgrimId)) {
                // Check user permissions
                const userRole = localStorage.getItem('userRole');
                const userId = localStorage.getItem('userId');
                
                let hasPermission = true;
                if (userRole === 'guide' && member.guideId !== userId) {
                  hasPermission = false;
                } else if (userRole === 'organizer' && member.organizerId !== userId) {
                  hasPermission = false;
                }
                
                if (!hasPermission) {
                  member.status = 'error';
                  member.result = 'You do not have permission to assign this pilgrim';
                  continue;
                }
                
                // Calculate age correctly using the dateOfBirth from pilgrim data
                const age = getAge(member.dateOfBirth);
                
                // Create bed assignment with proper guide and organizer information
                // Use the pilgrim's actual check-in and check-out dates as strings
                if (!member.checkInDate || !member.checkOutDate) {
                  throw new Error(`Missing check-in or check-out for ${member.pilgrimName} (${member.passport})`);
                }
                
                const assignment = {
                  pid: member.pilgrimId,
                  pn: member.pilgrimName || '',
                  ci: member.checkInDate, // Use string format DD/MM/YYYY
                  co: member.checkOutDate, // Use string format DD/MM/YYYY
                  g: member.gender === 'male' ? 'M' : member.gender === 'female' ? 'F' : 'U',
                  gid: member.guideId || '',
                  gn: member.guideName || '',
                  oid: member.organizerId || '',
                  on: member.organizerName || '',
                  age: age
                };
                
                // Update room and bed data
                if (!room.b[bedIndex].a) {
                  room.b[bedIndex].a = [];
                }
                
                room.b[bedIndex].a.push(assignment);
                room.o = (room.o || 0) + 1;
                
                // Mark accommodation in PilgrimSelector
                if (member.pilgrimId) {
                  const selectorRef = collection(firestore, 'PilgrimSelector');
                  const selectorQuery = query(selectorRef, where('pilgrimId', '==', member.pilgrimId));
                  const selectorSnapshot = await getDocs(selectorQuery);
                  
                  if (!selectorSnapshot.empty) {
                    const selectorDoc = selectorSnapshot.docs[0];
                    const selectorData = selectorDoc.data();
                    
                    // Update accommodation status
                    const updatedAccommodation = { ...selectorData.isAccommodated } || {};
                    updatedAccommodation[tableData.hn] = true;
                    
                    batch.update(selectorDoc.ref, {
                      isAccommodated: updatedAccommodation,
                      updatedAt: Date.now()
                    });
                  }
                }
                
                // Update row with status and result
                member.status = 'success';
                member.room = room.n;
                member.result = `Assigned to Room ${room.n}, Bed ${bedIndex + 1}, ${room.t === 'd' ? 'Double' : room.t === 't' ? 'Triple' : 'Quadruple'} room`;
                
                assignedToRoom++;
                assignedCount++;
                
                // Track processed IDs to avoid duplicates
                processedIds.add(member.pilgrimId);
              }
            }
            
            // If we assigned at least one family member, consider the room found
            if (assignedToRoom > 0) {
              roomFound = true;
              break;
            }
          }
        }
        
        // Update status for family members not assigned
        for (const member of family.members) {
          if (member.status === 'pending') {
            if (member.isAlreadyAccommodated) {
              member.status = 'error';
              member.result = 'Pilgrim is already accommodated in this hotel';
            } else if (!member.belongsToHotel) {
              member.status = 'error';
              member.result = 'Pilgrim is not assigned to this hotel in their package';
            } else if (!member.country) {
              member.status = 'error';
              member.result = 'Missing country information';
            } else if (hasMixedGenders && familySize < (member.roomType === 'double' ? 2 : member.roomType === 'triple' ? 3 : 4)) {
              member.status = 'error';
              member.result = 'Cannot assign mixed-gender family to a room unless fully occupying it';
            } else {
              member.status = 'error';
              member.result = roomFound
                ? 'Not enough space in family room'
                : roomTextFilter 
                  ? `No available ${member.roomType} rooms matching filter "${roomTextFilter}" for family`
                  : `No available ${member.roomType} rooms for family`;
            }
          }
        }
        
        // Update all rows to trigger UI refresh
        setRows(prev => [...prev]);
        setProcessedCount(prev => prev + family.members.length);
      }
      
      // Then, process individuals
      for (const individual of individuals) {
        const member = individual.members[0];
        
        // Skip if already processed, has error, or doesn't belong to the hotel
        if (!member.pilgrimId || 
            member.status === 'error' || 
            processedIds.has(member.pilgrimId) ||
            member.belongsToHotel !== true ||
            member.isAlreadyAccommodated === true ||
            !member.country) { // Ensure country exists
          
          if (member.isAlreadyAccommodated) {
            member.status = 'error';
            member.result = 'Pilgrim is already accommodated in this hotel';
          } else if (member.belongsToHotel === false) {
            member.status = 'error';
            member.result = 'Pilgrim is not assigned to this hotel in their package';
          } else if (!member.country) {
            member.status = 'error';
            member.result = 'Missing country information';
          }
          
          continue;
        }
        
        // Check user permissions
        const userRole = localStorage.getItem('userRole');
        const userId = localStorage.getItem('userId');
        
        let hasPermission = true;
        if (userRole === 'guide' && member.guideId !== userId) {
          hasPermission = false;
        } else if (userRole === 'organizer' && member.organizerId !== userId) {
          hasPermission = false;
        }
        
        if (!hasPermission) {
          member.status = 'error';
          member.result = 'You do not have permission to assign this pilgrim';
          continue;
        }
        
        const gender = member.gender;
        const country = member.country;
        const organizerId = member.organizerId;
        
        // Get individual pilgrim's date range and room type
        const pilgrimCheckIn = member.checkInDate || '';
        const pilgrimCheckOut = member.checkOutDate || '';
        const pilgrimRoomType = convertRoomTypeToShort(member.roomType || 'double');
        
        if (!pilgrimCheckIn || !pilgrimCheckOut) {
          // Skip pilgrim if we don't have valid dates
          member.status = 'error';
          member.result = 'Missing check-in or check-out date information';
          continue;
        }
        
        // Calculate age correctly using the dateOfBirth from pilgrim data
        const age = getAge(member.dateOfBirth);
        
        // Log pilgrim details for debugging
        console.log(`Processing individual ${member.pilgrimName}, room type: ${pilgrimRoomType}, gender: ${gender}, country: ${country}, check-in: ${pilgrimCheckIn}, check-out: ${pilgrimCheckOut}`);
        
        let roomFound = false;
        
        // Implementation of rules:
        // 2. All males -> goes to empty room
        // 3. All females -> goes to empty room
        // 5. Less than room capacity + one gender -> goes with same country mates
        // 6. Preference: find mates with same hotels and dates (same package)
        // 7. If no same country found -> goes to empty room
        
        // First pass - look for rooms with same country, gender, and package dates
        for (const room of filteredRooms) {
          // Skip rooms that don't match the pilgrim's room type
          if (room.t !== pilgrimRoomType) continue;
          
          // Skip fully occupied rooms
          if (room.o >= room.c) continue;
          
          // Check existing occupants during the pilgrim's stay period
          const currentOccupants = room.b
            .filter((bed: any) => bed.a && bed.a.length > 0)
            .flatMap((bed: any) => bed.a)
            .filter((a: any) => hasDateConflict(a, member.checkInTimestamp || 0, member.checkOutTimestamp || 0));
          
          // Skip if room is empty (we'll handle that in the next pass)
          if (currentOccupants.length === 0) continue;
          
          // Get gender of existing occupants
          const existingGender = currentOccupants[0].g;
          
          // Skip if gender doesn't match
          if ((gender === 'male' && existingGender !== 'M') || 
              (gender === 'female' && existingGender !== 'F')) {
            continue;
          }
          
          // Check country conflicts - IMPROVED to handle overlapping periods
          let hasCountryConflict = false;
          
          // Get countries of existing occupants using our pre-fetched map
          for (const occupant of currentOccupants) {
            const occupantCountry = pilgrimCountryMap.get(occupant.pid);
            // If any occupant has a country and it's different from our pilgrim, there's a conflict
            if (occupantCountry && occupantCountry !== country) {
              hasCountryConflict = true;
              break;
            }
          }
          
          if (hasCountryConflict) {
            console.log(`Skipping room ${room.n} due to country conflict during overlapping period`);
            continue;
          }
          
          // Check organizer conflicts if enabled
          let hasOrganizerConflict = false;
          if (sameOrganizerOnly) {
            const willFullyOccupyRoom = room.o + 1 === room.c;
            
            // Only check for organizer conflict if we're not fully occupying the room
            if (!willFullyOccupyRoom) {
              for (const occupant of currentOccupants) {
                if (occupant.oid && occupant.oid !== organizerId) {
                  hasOrganizerConflict = true;
                  break;
                }
              }
              
              if (hasOrganizerConflict) {
                console.log(`Skipping room ${room.n} due to organizer conflict - room has multiple organizers`);
                continue;
              }
            }
          }
          
          // Find an available bed
          for (let bedIndex = 0; bedIndex < room.b.length; bedIndex++) {
            const bed = room.b[bedIndex];
            
            // Skip beds that are already fully occupied
            if (bed.a && bed.a.length > 0) {
              // Check if the bed is available during this pilgrim's stay
              let hasConflict = false;
              for (const assignment of bed.a) {
                if (hasDateConflict(assignment, member.checkInTimestamp || 0, member.checkOutTimestamp || 0)) {
                  hasConflict = true;
                  break;
                }
              }
              
              if (hasConflict) continue;
            }
            
            // Found an available bed with same country and gender
            // Create assignment
            const assignment = {
              pid: member.pilgrimId,
              pn: member.pilgrimName || '',
              ci: member.checkInDate, // Use string format DD/MM/YYYY
              co: member.checkOutDate, // Use string format DD/MM/YYYY
              g: gender === 'male' ? 'M' : gender === 'female' ? 'F' : 'U',
              gid: member.guideId || '',
              gn: member.guideName || '',
              oid: member.organizerId || '',
              on: member.organizerName || '',
              age: age
            };
            
            console.log(`Assigning individual ${member.pilgrimName} to room ${room.n} bed ${bedIndex + 1} with compatible occupants`);
            
            // Update room and bed data
            if (!room.b[bedIndex].a) {
              room.b[bedIndex].a = [];
            }
            
            room.b[bedIndex].a.push(assignment);
            room.o = (room.o || 0) + 1;
            
            // Mark accommodation in PilgrimSelector
            if (member.pilgrimId) {
              const selectorRef = collection(firestore, 'PilgrimSelector');
              const selectorQuery = query(selectorRef, where('pilgrimId', '==', member.pilgrimId));
              const selectorSnapshot = await getDocs(selectorQuery);
              
              if (!selectorSnapshot.empty) {
                const selectorDoc = selectorSnapshot.docs[0];
                const selectorData = selectorDoc.data();
                
                // Update accommodation status
                const updatedAccommodation = { ...selectorData.isAccommodated } || {};
                updatedAccommodation[tableData.hn] = true;
                
                batch.update(selectorDoc.ref, {
                  isAccommodated: updatedAccommodation,
                  updatedAt: Date.now()
                });
              }
            }
            
            // Update row with status and result
            member.status = 'success';
            member.room = room.n;
            member.result = `Assigned to Room ${room.n}, Bed ${bedIndex + 1} with compatible occupants, ${room.t === 'd' ? 'Double' : room.t === 't' ? 'Triple' : 'Quadruple'} room`;
            
            assignedCount++;
            processedIds.add(member.pilgrimId);
            roomFound = true;
            break;
          }
          
          if (roomFound) break;
        }
        
        // Second pass - look for empty rooms (if not found in first pass)
        if (!roomFound) {
          for (const room of filteredRooms) {
            // Skip rooms that don't match the pilgrim's room type
            if (room.t !== pilgrimRoomType) continue;
            
            // Skip fully occupied rooms
            if (room.o >= room.c) continue;
            
            // Check if room is empty during the pilgrim's stay period
            const currentOccupants = room.b
              .filter((bed: any) => bed.a && bed.a.length > 0)
              .flatMap((bed: any) => bed.a)
              .filter((a: any) => hasDateConflict(a, member.checkInTimestamp || 0, member.checkOutTimestamp || 0));
            
            // Only consider empty rooms in this pass
            if (currentOccupants.length > 0) continue;
            
            // Find an available bed
            for (let bedIndex = 0; bedIndex < room.b.length; bedIndex++) {
              const bed = room.b[bedIndex];
              
              // Skip beds that are already occupied during this period
              if (bed.a && bed.a.length > 0) {
                let hasConflict = false;
                for (const assignment of bed.a) {
                  if (hasDateConflict(assignment, member.checkInTimestamp || 0, member.checkOutTimestamp || 0)) {
                    hasConflict = true;
                    break;
                  }
                }
                
                if (hasConflict) continue;
              }
              
              // Found an available bed in an empty room
              // Create assignment
              const assignment = {
                pid: member.pilgrimId,
                pn: member.pilgrimName || '',
                ci: member.checkInDate, // Use string format DD/MM/YYYY
                co: member.checkOutDate, // Use string format DD/MM/YYYY
                g: gender === 'male' ? 'M' : gender === 'female' ? 'F' : 'U',
                gid: member.guideId || '',
                gn: member.guideName || '',
                oid: member.organizerId || '',
                on: member.organizerName || '',
                age: age
              };
              
              console.log(`Assigning individual ${member.pilgrimName} to empty room ${room.n} bed ${bedIndex + 1}`);
              
              // Update room and bed data
              if (!room.b[bedIndex].a) {
                room.b[bedIndex].a = [];
              }
              
              room.b[bedIndex].a.push(assignment);
              room.o = (room.o || 0) + 1;
              
              // Mark accommodation in PilgrimSelector
              if (member.pilgrimId) {
                const selectorRef = collection(firestore, 'PilgrimSelector');
                const selectorQuery = query(selectorRef, where('pilgrimId', '==', member.pilgrimId));
                const selectorSnapshot = await getDocs(selectorQuery);
                
                if (!selectorSnapshot.empty) {
                  const selectorDoc = selectorSnapshot.docs[0];
                  const selectorData = selectorDoc.data();
                  
                  // Update accommodation status
                  const updatedAccommodation = { ...selectorData.isAccommodated } || {};
                  updatedAccommodation[tableData.hn] = true;
                  
                  batch.update(selectorDoc.ref, {
                    isAccommodated: updatedAccommodation,
                    updatedAt: Date.now()
                  });
                }
              }
              
              // Update row with status and result
              member.status = 'success';
              member.room = room.n;
              member.result = `Assigned to empty Room ${room.n}, Bed ${bedIndex + 1}, ${room.t === 'd' ? 'Double' : room.t === 't' ? 'Triple' : 'Quadruple'} room`;
              
              assignedCount++;
              processedIds.add(member.pilgrimId);
              roomFound = true;
              break;
            }
            
            if (roomFound) break;
          }
        }
        
        // If no room found, mark as error
        if (!roomFound) {
          member.status = 'error';
          member.result = roomTextFilter 
            ? `No available ${member.roomType} rooms matching filter "${roomTextFilter}" found for individual`
            : `No available ${member.roomType} rooms found for individual`;
        }
        
        // Update rows to trigger UI refresh
        setRows(prev => [...prev]);
        setProcessedCount(prev => prev + 1);
      }
      
      // Store the processed IDs for future reference
      setProcessedPilgrimIds(processedIds);
      
      // Sanitize rooms data to avoid undefined values before updating Firestore
      const sanitizedRooms = sanitizeRoomsData(rooms);
      
      // Update table with all changes
      const tableRef = doc(firestore, 'AccommodationTables', tableId);
      batch.update(tableRef, {
        r: sanitizedRooms,
        ua: Date.now()
      });
      
      // Commit all changes
      await batch.commit();
      
      console.log(`Successfully assigned ${assignedCount} pilgrims to rooms.`);
      setOverallProgress(100);
      
    } catch (error: any) {
      console.error('Error accommodating pilgrims:', error);
      // Update all pending rows with error
      setRows(prev => prev.map(row => 
        row.status === 'pending' 
          ? { ...row, status: 'error', result: `Accommodation error: ${error.message}` }
          : row
      ));
    }
  };

  useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, [isInitialized]);

  const getStatusBadge = (status: ProcessingStatus) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Pending</span>;
      case 'processing':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Processing</span>;
      case 'success':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Success</span>;
      case 'error':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Error</span>;
    }
  };

  const exportResults = () => {
    // Create worksheet data
    const wsData = rows.map(row => ({
      'Passport': row.passport,
      'Family Link': row.family_link || '-',
      'Gender': row.gender || '-',
      'Country': row.country || '-',
      'Room Type': row.roomType || '-',
      'Room': row.room || '-',
      'Status': row.status.charAt(0).toUpperCase() + row.status.slice(1),
      'Result': row.result || '-'
    }));
    
    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(wsData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // Passport
      { wch: 10 }, // Family Link
      { wch: 10 }, // Gender
      { wch: 15 }, // Country
      { wch: 15 }, // Room Type
      { wch: 8 },  // Room
      { wch: 12 }, // Status
      { wch: 50 }  // Result
    ];
    
    // Create workbook and add worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Accommodation Results');
    
    // Generate filename
    const now = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `bulk-accommodation-results-${now}.xlsx`;
    
    // Save file
    XLSX.writeFile(wb, filename);
  };

  // Calculate status counts
  const statusCounts = rows.reduce((acc, row) => {
    acc[row.status] = (acc[row.status] || 0) + 1;
    return acc;
  }, {} as Record<ProcessingStatus, number>);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-6 rounded-lg shadow-sm">
        <div>
          <h1 className="text-2xl font-bold">{translations?.bulkEasyAccommodation?.statusTitle || 'Easy Bulk Accommodation Status'}</h1>
          <p className="text-gray-500 mt-1">
            {tableName} ({hotelName})
            {roomTextFilter && (
              <span className="ml-2 text-primary-600 font-medium">
                • Filtering for rooms containing: "{roomTextFilter}"
              </span>
            )}
            {sameOrganizerOnly && (
              <span className="ml-2 text-blue-600 font-medium">
                • Only accommodating with same organizer
              </span>
            )}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft size={18} />
            <span>{translations?.bulkEasyAccommodation?.back || 'Back'}</span>
          </button>
          
          <button
            onClick={exportResults}
            className="flex items-center gap-2 px-4 py-2 bg-primary-100 text-primary-600 rounded-lg hover:bg-primary-200 transition-colors"
          >
            <FileText size={18} />
            <span>{translations?.bulkEasyAccommodation?.export || 'Export Results'}</span>
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="text-primary-500" size={20} />
            <h2 className="text-lg font-medium">{translations?.bulkEasyAccommodation?.progress || 'Progress'}</h2>
          </div>
          {isLoading && (
            <span className="text-blue-500 flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
              {currentStage}
            </span>
          )}
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-primary-500 h-2.5 rounded-full transition-all duration-500"
            style={{ width: `${overallProgress}%` }}  
          ></div>
        </div>
        
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-sm text-gray-500">Total</div>
            <div className="text-xl font-bold">{rows.length}</div>
          </div>
          
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="text-sm text-green-500">Success</div>
            <div className="text-xl font-bold">{statusCounts.success || 0}</div>
          </div>
          
          <div className="bg-red-50 p-3 rounded-lg">
            <div className="text-sm text-red-500">Errors</div>
            <div className="text-xl font-bold">{statusCounts.error || 0}</div>
          </div>
          
          <div className="bg-yellow-50 p-3 rounded-lg">
            <div className="text-sm text-yellow-500">Pending</div>
            <div className="text-xl font-bold">{statusCounts.pending || 0}</div>
          </div>
        </div>
      </div>

      {/* Search and filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by passport or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        </div>
      </div>

      {/* Results table */}
      <div className="bg-white rounded-lg shadow-sm overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Passport
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Family Link
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Gender
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Country
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Room Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Room
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Result
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredRows.length > 0 ? (
              filteredRows.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.passport}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.family_link || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.gender || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.country || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.roomType || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.room || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(row.status)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate">{row.result || '-'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                  {searchTerm ? 'No results found' : 'No data available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Error summary */}
      {statusCounts.error > 0 && (
        <div className="bg-red-50 border border-red-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="text-red-500" size={20} />
            <h3 className="font-medium text-red-800">
              {statusCounts.error} errors occurred during processing
            </h3>
          </div>
          <p className="text-red-700 text-sm">
            Common issues include: pilgrims not found in the system, no approved guide link, 
            pilgrims already accommodated in this hotel, pilgrims not assigned to this hotel in their package,
            missing country information, or no rooms available with the required room type.
            Please check the results column for specific error details.
          </p>
        </div>
      )}

      {/* Success summary */}
      {statusCounts.success > 0 && overallProgress === 100 && (
        <div className="bg-green-50 border border-green-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-green-500" size={20} />
            <h3 className="font-medium text-green-800">
              Successfully accommodated {statusCounts.success} pilgrims
              {roomTextFilter && ` in rooms containing "${roomTextFilter}"`}
              {sameOrganizerOnly && ' with same-organizer grouping enabled'}
            </h3>
          </div>
          <p className="text-green-700 text-sm">
            Pilgrims have been assigned to appropriate rooms based on their family groups and required room types.
            You can view the results in the table above or export them for reference.
          </p>
        </div>
      )}
    </div>
  );
};

export default BulkEasyAccommodationStatusPage;